<template>
  <div class="note-chat">
    <div class="chat-card">
      <div class="chat-header">
        <h4>Chat con operadores</h4>
        <span v-if="locked" class="chat-locked-pill">Solo lectura</span>
      </div>

      <div ref="scroller" class="chat-messages">
        <div v-if="loading" class="chat-status">Cargando chat…</div>
        <div v-else-if="loadError" class="chat-status">
          No se pudo cargar el chat.
          <button class="chat-retry" @click="fetchThread()">Reintentar</button>
        </div>
        <div v-else-if="messages.length === 0" class="chat-status">
          Aún no hay mensajes en esta nota.
        </div>
        <div
          v-for="message in messages"
          :key="message.id"
          class="chat-row"
          :class="message.from_operator ? 'chat-row--operator' : 'chat-row--office'">
          <div
            class="chat-bubble"
            :class="message.from_operator ? 'chat-bubble--operator' : 'chat-bubble--office'">
            <div class="chat-sender">
              {{ message.sender_name }}
              <span class="chat-side-tag">{{ message.from_operator ? 'Operador' : 'Oficina' }}</span>
            </div>
            <div class="chat-body">{{ message.body }}</div>
            <div class="chat-time">{{ formatTime(message.created_at) }}</div>
          </div>
        </div>
      </div>

      <div v-if="locked" class="chat-locked-banner">
        La nota está cerrada; ya no se pueden enviar mensajes. El historial queda como registro.
      </div>
      <div v-else class="chat-input-row">
        <textarea
          v-model="draft"
          class="chat-input"
          rows="2"
          maxlength="2000"
          placeholder="Escribe un mensaje para los operadores…"
          @keydown.enter.exact.prevent="send"
        ></textarea>
        <button class="chat-send" :disabled="!draft.trim() || sending" @click="send">
          {{ sending ? 'Enviando…' : 'Enviar' }}
        </button>
      </div>
      <div v-if="sendError" class="chat-send-error">{{ sendError }}</div>
    </div>
  </div>
</template>

<script>
import axios from 'axios';
import { useDashboardWS } from '../composables/useDashboardWS';

const apiDirectory = process.env.API_DIRECTORY;

export default {
  name: 'NoteChat',
  props: {
    noteIdent: {
      type: String,
      required: true,
    },
  },
  emits: ['read'],
  setup() {
    // This component only mounts for authenticated users with a chat open,
    // so the socket can connect for its whole lifetime.
    const { status: chatWsStatus, onMessage: onChatWsMessage } = useDashboardWS('chat');
    return { chatWsStatus, onChatWsMessage };
  },
  data() {
    return {
      messages: [],
      locked: false,
      loading: true,
      loadError: false,
      draft: '',
      sending: false,
      sendError: '',
      refetchTimer: null,
    };
  },
  watch: {
    chatWsStatus(value) {
      // Catch up whenever the socket (re)opens: pings may have been missed
      // while offline, and with no fallback poll this is the only recovery
      // path besides the tab regaining visibility.
      if (value === 'open' && !this.loading) {
        this.scheduleRefetch();
      }
    },
  },
  created() {
    // Pure push: one fetch for the initial thread, then websocket pings,
    // reconnects and tab visibility drive every refresh. No polling.
    this.fetchThread();
    this.onChatWsMessage((msg) => {
      if (msg && msg.topic === 'note_chat_message' && msg.note_ident === this.noteIdent) {
        this.scheduleRefetch();
      }
    });
    document.addEventListener('visibilitychange', this.onVisibilityChange);
  },
  beforeUnmount() {
    if (this.refetchTimer) clearTimeout(this.refetchTimer);
    document.removeEventListener('visibilitychange', this.onVisibilityChange);
  },
  methods: {
    authHeaders() {
      return { Authorization: 'Token ' + localStorage.getItem('authToken') };
    },
    formatTime(iso) {
      if (!iso) return '';
      const date = new Date(iso);
      if (Number.isNaN(date.getTime())) return '';
      return date.toLocaleString('es-MX', {
        day: '2-digit',
        month: 'short',
        hour: 'numeric',
        minute: '2-digit',
        hour12: true,
      });
    },
    onVisibilityChange() {
      // Coming back to the tab counts as reading the messages on screen.
      if (!document.hidden) this.fetchThread({ silent: true });
    },
    scheduleRefetch() {
      // Coalesce ping bursts with a little jitter so participants of a busy
      // thread don't all refetch at the same instant.
      if (this.refetchTimer) return;
      this.refetchTimer = setTimeout(() => {
        this.refetchTimer = null;
        this.fetchThread({ silent: true });
      }, 100 + Math.random() * 400);
    },
    async fetchThread(options) {
      if (!options || !options.silent) {
        this.loading = true;
      }
      this.loadError = false;
      // Only advance the read cursor while the user can actually see the
      // thread. A chat left open in a hidden tab must NOT swallow unread
      // badges (the global bubble would ring with nothing to show).
      const markRead = !document.hidden;
      try {
        const response = await axios.get(
          `${apiDirectory}/notes/${this.noteIdent}/chat/${markRead ? '?mark_read=1' : ''}`,
          { headers: this.authHeaders() }
        );
        const previousCount = this.messages.length;
        this.messages = response.data.messages;
        this.locked = response.data.locked;
        if (markRead) this.$emit('read');
        if (this.messages.length !== previousCount) {
          this.scrollToBottom();
        }
      } catch (error) {
        if (!options || !options.silent) {
          this.loadError = true;
        }
      } finally {
        this.loading = false;
      }
    },
    async send() {
      const body = this.draft.trim();
      if (!body || this.sending || this.locked) return;
      this.sending = true;
      this.sendError = '';
      try {
        const response = await axios.post(
          `${apiDirectory}/notes/${this.noteIdent}/chat/`,
          { body },
          { headers: this.authHeaders() }
        );
        this.draft = '';
        this.messages.push(response.data);
        this.scrollToBottom();
      } catch (error) {
        if (error?.response?.status === 409) {
          this.locked = true;
        } else {
          this.sendError =
            error?.response?.data?.detail || 'No se pudo enviar el mensaje.';
        }
      } finally {
        this.sending = false;
      }
    },
    scrollToBottom() {
      this.$nextTick(() => {
        const scroller = this.$refs.scroller;
        if (scroller) scroller.scrollTop = scroller.scrollHeight;
      });
    },
  },
};
</script>

<style scoped>
.note-chat {
  font-family: 'Poppins', sans-serif;
  padding: 15px 0;
}

.chat-card {
  background: white;
  border: 1px solid #e4e4e4;
  border-left: 4px solid #d5171d;
  border-radius: 10px;
  max-width: 760px;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.chat-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 16px;
  border-bottom: 1px solid #efefef;
}

.chat-header h4 {
  margin: 0;
  font-size: 15px;
}

.chat-locked-pill {
  background: #f2f2f2;
  color: #777;
  font-size: 11px;
  padding: 3px 10px;
  border-radius: 10px;
}

.chat-messages {
  height: 420px;
  overflow-y: auto;
  padding: 14px 16px;
  background: #fafafa;
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.chat-status {
  color: #999;
  font-size: 13px;
  text-align: center;
  margin-top: 24px;
}

.chat-retry {
  margin-left: 8px;
  padding: 3px 12px;
  background: #d5171d;
  color: white;
  border: none;
  border-radius: 6px;
  cursor: pointer;
  font-size: 12px;
}

.chat-row {
  display: flex;
}

.chat-row--office {
  justify-content: flex-end;
}

.chat-row--operator {
  justify-content: flex-start;
}

.chat-bubble {
  max-width: 78%;
  border-radius: 10px;
  padding: 8px 12px;
  font-size: 13px;
}

.chat-bubble--office {
  background: #ffc434;
  color: #1a1a1a;
}

.chat-bubble--operator {
  background: white;
  border: 1px solid #e4e4e4;
  color: #1a1a1a;
}

.chat-sender {
  font-size: 11px;
  font-weight: 600;
  margin-bottom: 2px;
  color: #d5171d;
}

.chat-bubble--office .chat-sender {
  color: rgba(0, 0, 0, 0.6);
}

.chat-side-tag {
  font-weight: 400;
  color: #999;
  margin-left: 4px;
  font-size: 10px;
}

.chat-body {
  white-space: pre-wrap;
  word-break: break-word;
  line-height: 1.45;
}

.chat-time {
  font-size: 10px;
  color: rgba(0, 0, 0, 0.45);
  text-align: right;
  margin-top: 3px;
}

.chat-locked-banner {
  padding: 12px 16px;
  background: #f8f8f8;
  color: #888;
  font-size: 12px;
  border-top: 1px solid #efefef;
  text-align: center;
}

.chat-input-row {
  display: flex;
  gap: 8px;
  padding: 12px 16px;
  border-top: 1px solid #efefef;
  align-items: flex-end;
}

.chat-input {
  flex: 1;
  resize: none;
  border: 1px solid #ddd;
  border-radius: 8px;
  padding: 8px 12px;
  font-family: 'Poppins', sans-serif;
  font-size: 13px;
}

.chat-input:focus {
  outline: none;
  border-color: #ffc434;
}

.chat-send {
  padding: 9px 22px;
  background: #d5171d;
  color: white;
  border: none;
  border-radius: 7px;
  cursor: pointer;
  font-family: 'Poppins', sans-serif;
  font-size: 13px;
}

.chat-send:hover:enabled {
  background: black;
}

.chat-send:disabled {
  background: #ccc;
  cursor: not-allowed;
}

.chat-send-error {
  color: #d5171d;
  font-size: 12px;
  padding: 0 16px 12px;
}
</style>
